/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import type { Node } from 'react';
import React, { createContext, useReducer, useState } from 'react';

import { BOI_WALLET_USER_NAME_STORAGE } from './constants';
import { HOME_CUSTOMER_GIVEN_NAME } from './constants/sessionStorage';
import {
  BOI_WALLET_USER_STORAGE,
  USER_BRANCH
} from './constants/sessionStorage/userStorageIdentifiers';
import { getItemFromSessionStorage, saveInSessionStorage } from './helpers';
import type { UserContextType } from './types';

type ActionTypes = 'setFirstName' | 'setBranchName' | 'default';

type State = {
  firstName?: string,
  branchName?: any
};

type Action = {
  type: ActionTypes,
  payload: {
    firstName?: string,
    branchName?: any
  }
};

type ActionsObject = {
  [key: ActionTypes]: () => State
};

type Props = {
  children: Node,
  initialValue?: {
    firstName?: string,
    branchName?: any
  }
};

const emptyFirstName: State = { firstName: '' };
const emptyBranchName: State = { branchName: {} };

const UserContext: React$Context<UserContextType> = createContext<any>([
  emptyFirstName,
  (a: Action) => {
    reducer(emptyFirstName, a);
  },
  emptyBranchName,
  (a: Action) => {
    reducer(emptyBranchName, a);
  }
]);

const setBranchNameData = (action: Action, state: State) => {
  saveInSessionStorage(USER_BRANCH, JSON.stringify(action.payload.branchName));
  return {
    ...state,
    branchName: action.payload.branchName
  };
};

const reducer = (state: State, action: Action) => {
  const actions: ActionsObject = {
    setFirstName: () => ({
      ...state,
      firstName: action.payload.firstName
    }),
    setBranchName: () => setBranchNameData(action, state),
    default: () => state
  };

  if (actions.hasOwnProperty(action.type)) {
    return actions[action.type]();
  }

  return actions.default();
};

export const UserContextProvider = ({ children, initialValue }: Props) => {
  const initalState = initialValue || { firstName: '', branchName: {} };

  const [user, userDispatch] = useReducer(reducer, initalState);
  const [agentScriptPending, setAgentScriptPending] = useState(false);

  const getNameFromStorage = () =>
    getItemFromSessionStorage(BOI_WALLET_USER_NAME_STORAGE) ||
    JSON.parse(getItemFromSessionStorage(BOI_WALLET_USER_STORAGE));

  const getBranchNameFromStorage = () => {
    return JSON.parse(getItemFromSessionStorage(USER_BRANCH));
  };

  // eslint-disable-next-line flowtype/require-parameter-type
  const retrieveGivenName = (storageName) => {
    const customerName = getItemFromSessionStorage(HOME_CUSTOMER_GIVEN_NAME);
    return customerName.length > 0 ? customerName : storageName?.attributes?.given_name;
  };

  const getUserName = () => {
    if (user.firstName) return user.firstName;
    const storageName = getNameFromStorage();
    if (storageName) {
      const firstName = retrieveGivenName(storageName);
      userDispatch({
        type: 'setFirstName',
        payload: { firstName: firstName }
      });
      return firstName;
    }
    return '';
  };

  const getBranchName = () => {
    if (user.branchName) return user.branchName;
    const storageBranchName = getBranchNameFromStorage();
    if (storageBranchName) {
      userDispatch({
        type: 'setBranchName',
        payload: { branchName: storageBranchName }
      });
      return storageBranchName;
    }
    return '';
  };

  const value = {
    getUserName,
    getBranchName,
    user,
    userDispatch,
    agentScriptPending,
    setAgentScriptPending
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
